import { useState } from 'react';
import { Modal, Spinner, Badge, MobileCardList } from '../common';
import { useStorageSummary, useInventoryTransactions } from '../../hooks/useInventory';
import { StorageSummaryFilters } from '../../types';
import { formatDateTime } from '../../utils/dateTime';

interface StorageSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: StorageSummaryFilters | null;
}

export default function StorageSummaryModal({ isOpen, onClose, filters }: StorageSummaryModalProps) {
  const [activeTab, setActiveTab] = useState<'storage' | 'history'>('storage');
  
  const { data: storageData, isLoading, isError, error } = useStorageSummary(filters);
  
  // Fetch transaction history for this specific inventory entry (client + product + batch)
  const { data: transactionData, isLoading: transactionsLoading, isError: transactionsError } = useInventoryTransactions(
    {
      clientId: filters?.clientId,
      productDescription: filters?.productDescription,
      batchNo: filters?.batchNo,
      limit: 100,
    },
    isOpen && activeTab === 'history' && !!filters
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Product Details" size="xl">
      <div className="space-y-4">
        {filters && (
          <div className="bg-gradient-to-r from-brand-50 to-brand-100 p-4 rounded-lg border-2 border-brand-200 space-y-2">
            <div className="text-sm">
              <span className="font-semibold text-brand-900">Product:</span>{' '}
              <span className="text-brand-800">{filters.productDescription}</span>
            </div>
            <div className="text-sm">
              <span className="font-semibold text-brand-900">Batch No:</span>{' '}
              <span className="text-brand-800">{filters.batchNo}</span>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('storage')}
              className={`flex-1 py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'storage'
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-warehouse mr-2"></i>
              Storage Locations
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-3 px-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'history'
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-history mr-2"></i>
              Transaction History
            </button>
          </nav>
        </div>

        {/* Storage Tab Content */}
        {activeTab === 'storage' && (
          <>
            {isLoading && (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            )}

            {isError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  {(error as any)?.message || 'Failed to load storage summary'}
                </p>
              </div>
            )}

            {!isLoading && !isError && (!storageData || storageData.length === 0) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-start">
                  <i className="fas fa-info-circle text-yellow-600 mr-3 mt-1"></i>
                  <div>
                    <p className="text-sm font-medium text-yellow-900 mb-1">No storage locations found</p>
                    <p className="text-sm text-yellow-800">
                      This product may not have any physical storage assigned yet.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!isLoading && !isError && storageData && storageData.length > 0 && (
              <>
              <MobileCardList
                columns={[
                  { key: 'clientName', label: 'Client' },
                  {
                    key: 'storageType',
                    label: 'Storage Type',
                    render: (value, row: any) => (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        row?.isLbd ? 'bg-red-100 text-red-700' : value === 'rack' ? 'bg-brand-100 text-brand-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {row?.isLbd ? 'LBD' : value}
                      </span>
                    ),
                  },
                  { key: 'zoneCode', label: 'Zone', render: (_value, row: any) => row.zoneCode || '-' },
                  { key: 'zoneLocationCode', label: 'Zone Location', render: (_value, row: any) => row.zoneLocationCode || '-' },
                  { key: 'rackCode', label: 'Rack' },
                  { key: 'locationCode', label: 'Location' },
                  { key: 'palletCount', label: 'Pallet Count', render: (value) => <span className="font-medium">{value}</span> },
                ]}
                data={storageData}
              />
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Client
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Storage Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Zone
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Zone Location
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Rack
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                        Location
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">
                        Pallet Count
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {storageData.map((item, index) => (
                      <tr key={index} className="hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent hover:shadow-md hover:scale-[1.01] transform transition-all duration-300 border-b border-gray-200 last:border-b-0 group">
                        <td className="px-4 py-3 text-sm text-gray-900 group-hover:text-primary-600 transition-colors duration-200">{item.clientName || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium shadow-sm group-hover:shadow-md transition-shadow duration-200 ${
                            item.isLbd ? 'bg-red-100 text-red-700' : item.storageType === 'rack' ? 'bg-brand-100 text-brand-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.isLbd ? 'LBD' : item.storageType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.zoneCode || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.zoneLocationCode || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.rackCode || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.locationCode || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                          {item.palletCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </>
        )}

        {/* History Tab Content */}
        {activeTab === 'history' && (
          <>
            {transactionsLoading && (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            )}

            {transactionsError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">Failed to load transaction history</p>
              </div>
            )}

            {!transactionsLoading && !transactionsError && (!transactionData?.data || transactionData.data.length === 0) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-start">
                  <i className="fas fa-info-circle text-yellow-600 mr-3 mt-1"></i>
                  <div>
                    <p className="text-sm font-medium text-yellow-900 mb-1">No transaction history found</p>
                    <p className="text-sm text-yellow-800">
                      There are no inward or outward transactions recorded for this product and batch.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!transactionsLoading && !transactionsError && transactionData?.data && transactionData.data.length > 0 && (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {transactionData.data.map((transaction, index) => (
                  <div
                    key={index}
                    className={`border-l-4 rounded-lg p-4 shadow-sm ${
                      transaction.transactionType?.toLowerCase() === 'inward'
                        ? 'bg-green-50 border-green-500'
                        : 'bg-red-50 border-red-500'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          transaction.transactionType?.toLowerCase() === 'inward'
                            ? 'bg-green-100'
                            : 'bg-red-100'
                        }`}>
                          <i className={`fas ${
                            transaction.transactionType?.toLowerCase() === 'inward'
                              ? 'fa-arrow-down text-green-600'
                              : 'fa-arrow-up text-red-600'
                          } text-lg`}></i>
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <Badge
                              variant={transaction.transactionType?.toLowerCase() === 'inward' ? 'success' : 'warning'}
                              rounded
                            >
                              {transaction.transactionType?.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {transaction.referenceType}-{transaction.referenceId}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            {formatDateTime(transaction.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Good Qty</p>
                        <p className={`text-sm font-semibold ${
                          transaction.transactionType?.toLowerCase() === 'inward'
                            ? 'text-green-700'
                            : 'text-red-700'
                        }`}>
                          {transaction.transactionType?.toLowerCase() === 'inward' ? '+' : '-'}
                          {Math.abs(transaction.goodQty || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">CDAM Qty</p>
                        <p className={`text-sm font-semibold ${
                          transaction.transactionType?.toLowerCase() === 'inward'
                            ? 'text-green-700'
                            : 'text-red-700'
                        }`}>
                          {transaction.transactionType?.toLowerCase() === 'inward' ? '+' : '-'}
                          {Math.abs(transaction.cdamQty || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">DAM Qty</p>
                        <p className={`text-sm font-semibold ${
                          transaction.transactionType?.toLowerCase() === 'inward'
                            ? 'text-green-700'
                            : 'text-red-700'
                        }`}>
                          {transaction.transactionType?.toLowerCase() === 'inward' ? '+' : '-'}
                          {Math.abs(transaction.damQty || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Pallets</p>
                        <p className={`text-sm font-semibold ${
                          transaction.transactionType?.toLowerCase() === 'inward'
                            ? 'text-brand-700'
                            : 'text-orange-700'
                        }`}>
                          {transaction.transactionType?.toLowerCase() === 'inward' ? '+' : '-'}
                          {Math.abs(transaction.palletCount || 0)}
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-gray-500">Created By:</span>{' '}
                          <span className="font-medium text-gray-900">{transaction.createdByName}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Category:</span>{' '}
                          <span className="font-medium text-gray-900">{transaction.productCategoryName}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
